import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Check, Pencil, X } from "lucide-react";
import { type FormEvent, useState } from "react";

/**
 * The box editor's title, renamed in place: a pencil on hover opens an input with Save, which
 * writes the name alone (`boxes.update`), apart from the draft's Save / Publish.
 */
export function BoxNameHeading({
  name,
  editable,
  pending,
  onRename,
}: {
  name: string;
  /** False for viewers and archived boxes: the heading is plain text. */
  editable: boolean;
  pending: boolean;
  /** Saves the trimmed name; `done` runs once the write succeeded. */
  onRename: (name: string, done: () => void) => void;
}) {
  const [value, setValue] = useState<string | null>(null);

  if (value === null) {
    return (
      <div className="group flex min-w-0 items-center gap-1">
        <h1 className="truncate text-2xl font-semibold tracking-tight">{name}</h1>
        {editable && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setValue(name)}
            title="Rename"
            // Always reachable by keyboard; shown on hover or focus.
            className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          >
            <Pencil />
            <span className="sr-only">Rename</span>
          </Button>
        )}
      </div>
    );
  }

  const trimmed = value.trim();
  const canSave = trimmed.length > 0 && trimmed !== name && !pending;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (canSave) onRename(trimmed, () => setValue(null));
  };

  return (
    <form
      onSubmit={submit}
      onKeyDown={(event) => {
        if (event.key === "Escape") setValue(null);
      }}
      className="flex min-w-0 items-center gap-2"
    >
      <Input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        maxLength={200}
        aria-label="Box name"
        autoFocus
        className="h-8 max-w-md text-lg font-semibold"
      />
      <Button type="submit" size="sm" disabled={!canSave}>
        <Check />
        {pending ? "Saving…" : "Save"}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => setValue(null)}
        title="Cancel (Esc)"
      >
        <X />
        <span className="sr-only">Cancel</span>
      </Button>
    </form>
  );
}
